import { makeCygnusApiCall } from "../../api/api";
import {
  AddUserFormField,
  FeatureData,
  RoleData,
  ProductData,
  ProductFeatureData,
  ProductAwareUserRowData,
  SubscriptionPackageData,
  UpdateUserAccessPayload,
  FirmSpecialPrivilegesResponse,
} from "@cygnusaiinternal/cygnus-common";

type UserRowWithFirmIdentifiers = ProductAwareUserRowData & {
  FirmId?: number | string | null;
  FirmDetailId?: number | string | null;
  UserName?: string;
  FirmDetailID?: number | string | null;
  FirmDetailid?: number | string | null;
  firm_detail_id?: number | string | null;
  username?: string;
  [key: string]: unknown;
};

type FirmPackageAssignmentData = {
  FirmId?: number | string | null;
  PackageId?: number | string | null;
  Active?: "Y" | "N" | boolean | string | null;
  [key: string]: unknown;
};

type RoleFeaturesMatrixData = {
  FirmId?: number;
  FirmName?: string;
  PackageId?: number;
  PackageName?: string;
  RequestedBy?: string;
  UpgradePackageName?: string;
  Features?: FeatureData[];
  Roles?: RoleData[];
  Packages?: SubscriptionPackageData[];
};

const roleFeaturesMatrixRequests = new Map<number, Promise<RoleFeaturesMatrixData | null>>();

const getStoredFirmId = (): number =>
  parseInt(
    localStorage.getItem("FirmId") ?? localStorage.getItem("firmId") ?? "0",
    10
  );

const parseOptionalNumber = (value: unknown): number | null => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeUserProductAccessRow = (
  row: ProductAwareUserRowData,
  firmId: number
): ProductAwareUserRowData => {
  const rowWithIdentifiers = row as UserRowWithFirmIdentifiers;
  const normalizedFirmId =
    parseOptionalNumber(rowWithIdentifiers.FirmId) ??
    parseOptionalNumber(rowWithIdentifiers.firm_id) ??
    parseOptionalNumber(rowWithIdentifiers["Firm Id"]) ??
    firmId;
  const normalizedFirmDetailId =
    parseOptionalNumber(rowWithIdentifiers.FirmDetailId) ??
    parseOptionalNumber(rowWithIdentifiers.FirmDetailID) ??
    parseOptionalNumber(rowWithIdentifiers.FirmDetailid) ??
    parseOptionalNumber(rowWithIdentifiers.firm_detail_id);
  const normalizedUserName =
    rowWithIdentifiers.UserName ??
    rowWithIdentifiers.username ??
    (typeof rowWithIdentifiers["User Name"] === "string"
      ? rowWithIdentifiers["User Name"]
      : undefined);

  return {
    ...row,
    FirmId: normalizedFirmId,
    FirmDetailId: normalizedFirmDetailId,
    UserName: normalizedUserName,
  } as ProductAwareUserRowData;
};

const toBooleanOrUndefined = (value: unknown): boolean | undefined => {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.toLowerCase();
    if (["y", "yes", "true", "1"].includes(normalized)) {
      return true;
    }
    if (["n", "no", "false", "0"].includes(normalized)) {
      return false;
    }
  }

  if (typeof value === "number") {
    return value === 1;
  }

  return undefined;
};

const isProductAvailable = (product: ProductData): boolean => {
  const effective = toBooleanOrUndefined(product.Effective);
  if (effective !== undefined) {
    return effective;
  }

  const enabled = toBooleanOrUndefined(product.Enabled);
  if (enabled !== undefined) {
    return enabled;
  }

  const active = toBooleanOrUndefined(product.Active);
  return active ?? true;
};

const normalizeFeature = (feature: FeatureData): ProductFeatureData => {
  const source = feature as ProductFeatureData & Record<string, unknown>;
  const products = Array.isArray(source.Products) ? source.Products : [];
  const included =
    toBooleanOrUndefined(source.IncludedInCurrentPackage) ??
    toBooleanOrUndefined(source.AvailableInCurrentPackage) ??
    (products.length > 0 ? products.some(isProductAvailable) : undefined);
  const locked = toBooleanOrUndefined(source.Locked) ?? (included === false);
  const productCode =
    source.ProductCode ??
    source.Product_Code ??
    products.find((product) => product.ProductCode)?.ProductCode;
  const featureCode = source.FeatureCode ?? source.Feature_Code;

  return {
    ...feature,
    FeatureCode: typeof featureCode === "string" ? featureCode : undefined,
    ProductCode: typeof productCode === "string" ? productCode : undefined,
    IncludedInCurrentPackage: included,
    AvailableInCurrentPackage: included,
    Locked: locked,
    UpgradePackageName:
      typeof source.UpgradePackageName === "string"
        ? source.UpgradePackageName
        : typeof source.Upgrade_Package_Name === "string"
          ? source.Upgrade_Package_Name
          : undefined,
  };
};

const normalizeRole = (role: RoleData): RoleData => ({
  ...role,
  FeaturesList: (role.FeaturesList ?? []).map(normalizeFeature),
});

export async function getRoleFeaturesMatrix(
  firmId: number
): Promise<RoleFeaturesMatrixData | null> {
  const matrix = await getMatrixData(firmId);
  if (!matrix) {
    return null;
  }

  return {
    ...matrix,
    Features: (matrix.Features ?? []).map(normalizeFeature),
    Roles: (matrix.Roles ?? []).map(normalizeRole),
  };
}

const getMatrixData = async (firmId: number): Promise<RoleFeaturesMatrixData | null> => {
  const pendingRequest = roleFeaturesMatrixRequests.get(firmId);
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/subscriptions/roles/features-matrix/?FirmId=${firmId}`,
    show_message: false,
    customized_success_message: "Successfully retrieved role feature matrix.",
    customized_failure_message:
      "Unable to fetch role feature matrix, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while fetching role feature matrix, please contact support.",
    isblob: false,
  })
    .then((data) => data?.data ?? null)
    .finally(() => {
      roleFeaturesMatrixRequests.delete(firmId);
    });

  roleFeaturesMatrixRequests.set(firmId, request);
  return request;
};

// ============================================
// USER MANAGEMENT
// ============================================

export async function getAddUserFormSchema(): Promise<AddUserFormField[] | null> {
  const data = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/add/user/`,
    show_message: false,
    customized_success_message: "Successfully retrieved your data.",
    customized_failure_message:
      "Unable to fetch details for adding a user, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while fetching details for adding a user, please contact support.",
    isblob: false,
  });

  return data?.success ? data.data : null;
}

export async function addUser(payload: {
  first_name: string;
  last_name: string;
  username: string;
  password: string;
  email: string;
  role: string;
  Products?: Array<{
    ProductReferenceDataId?: number;
    ProductCode: string;
    ProductName?: string;
    Enabled: boolean;
    Active: "Y" | "N";
  }>;
}): Promise<boolean> {
  const data = await makeCygnusApiCall({
    type: "POST",
    payload,
    url: `/add/user/`,
    show_message: false,
    customized_success_message: "Successfully added user details.",
    customized_failure_message:
      "Unable to add user details, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while adding user details, please contact support.",
    isblob: false,
  });

  return !!data?.success;
}

export async function updateUserRole(
  userName: string,
  role: string
): Promise<boolean> {
  const payload = {
    data: [{ UserName: userName, Role: role }],
  };

  const data = await makeCygnusApiCall({
    type: "POST",
    payload,
    url: `/subscriptions/roles/users/`,
    show_message: true,
    customized_success_message: "Successfully saved user details.",
    customized_failure_message:
      "Unable to save user details, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while saving user details, please contact support.",
    isblob: false,
  });

  return !!data?.success;
}

export async function updateUserStatus(
  username: string,
  active: "Y" | "N" | "L"
): Promise<boolean> {
  const data = await makeCygnusApiCall({
    type: "POST",
    payload: { username, active },
    url: `/user/disable/`,
    show_message: true,
    customized_success_message: "Successfully saved user status.",
    customized_failure_message:
      "Unable to save user status, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while saving user status, please contact support.",
    isblob: false,
  });

  return !!data?.success;
}

// ============================================
// FEATURES & SUBSCRIPTIONS
// ============================================

export async function getAllFeatures(
  firmId?: number
): Promise<ProductFeatureData[] | null> {
  if (firmId) {
    const matrix = await getMatrixData(firmId);
    if (matrix?.Features) {
      return matrix.Features.map(normalizeFeature);
    }
  }

  const data = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/subscriptions/all_features/`,
    show_message: false,
    customized_success_message: "Successfully retrieved all features.",
    customized_failure_message:
      "Unable to fetch all features, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while fetching all features, please contact support.",
    isblob: false,
  });

  return data?.success ? (data.data ?? []).map(normalizeFeature) : null;
}

// ============================================
// ROLES
// ============================================

export async function getUserRoles(): Promise<any[] | null> {
  const data = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/subscriptions/roles/users/`,
    show_message: false,
    customized_success_message: "Successfully retrieved all user roles.",
    customized_failure_message:
      "Unable to fetch all user roles, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while fetching all user roles, please contact support.",
    isblob: false,
  });

  return data?.success ? data.data : null;
}

export async function getAllRoles(firmId?: number): Promise<RoleData[] | null> {
  if (firmId) {
    const matrix = await getMatrixData(firmId);
    if (matrix?.Roles) {
      return matrix.Roles.map(normalizeRole);
    }
  }

  const data = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/subscriptions/roles/`,
    show_message: false,
    customized_success_message: "Successfully retrieved all available roles.",
    customized_failure_message:
      "Unable to fetch all available roles, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while fetching all available roles, please contact support.",
    isblob: false,
  });

  return data?.success ? (data.data ?? []).map(normalizeRole) : null;
}

export async function updateRoleFeatures(
  roleName: string,
  featureCodes: string[],
  productCode?: string,
  roleId?: number
): Promise<boolean> {
  const payload: Record<string, unknown> = {
    FirmId: getStoredFirmId(),
    Features: featureCodes,
  };

  if (roleId !== undefined) {
    payload.RoleId = roleId;
  } else {
    payload.Role = roleName;
  }

  if (productCode !== undefined) {
    payload.ProductCode = productCode;
  }

  const data = await makeCygnusApiCall({
    type: "POST",
    payload,
    url: `/subscriptions/roles/update/`,
    show_message: true,
    customized_success_message: "Successfully updated role features.",
    customized_failure_message:
      "Unable to update role features, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while updating role features, please contact support.",
    isblob: false,
  });

  return !!data?.success;
}

// ============================================
// PRODUCTS
// ============================================

export async function getProducts(): Promise<ProductData[] | null> {
  const data = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/subscriptions/products/`,
    show_message: false,
    customized_success_message: "Successfully retrieved products.",
    customized_failure_message:
      "Unable to fetch products, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while fetching products, please contact support.",
    isblob: false,
  });

  return data?.success ? data.data : null;
}

export async function getAvailablePackages(
  firmId?: number
): Promise<SubscriptionPackageData[] | null> {
  if (firmId) {
    const matrix = await getMatrixData(firmId);
    if (matrix?.Packages) {
      return matrix.Packages;
    }
  }

  const data = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/subscriptions/packages/`,
    show_message: false,
    customized_success_message: "Successfully retrieved packages.",
    customized_failure_message:
      "Unable to fetch packages, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while fetching packages, please contact support.",
    isblob: false,
  });

  return data?.data ?? data ?? null;
}

export async function getFirmPackageAssignments(): Promise<FirmPackageAssignmentData[] | null> {
  const data = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/subscriptions/packages/firms/`,
    show_message: false,
    customized_success_message: "Successfully retrieved firm package assignments.",
    customized_failure_message:
      "Unable to fetch firm package assignments, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while fetching firm package assignments, please contact support.",
    isblob: false,
  });

  return data?.data ?? data ?? null;
}

export async function getCurrentPackage(
  firmId: number
): Promise<SubscriptionPackageData | null> {
  const matrix = await getMatrixData(firmId);
  if (matrix?.PackageId || matrix?.PackageName) {
    return {
      PackageId: matrix.PackageId,
      PackageName: matrix.PackageName ?? "",
      Current: true,
    };
  }

  const [packages, firmPackages] = await Promise.all([
    getAvailablePackages(firmId),
    getFirmPackageAssignments(),
  ]);

  const activeFirmPackage = (firmPackages ?? []).find(
    (row) =>
      Number(row.FirmId) === Number(firmId) &&
      (row.Active === "Y" || row.Active === true)
  );

  if (!activeFirmPackage) {
    return null;
  }

  const currentPackage =
    (packages ?? []).find(
      (pkg) => Number(pkg.PackageId) === Number(activeFirmPackage.PackageId)
    ) ?? null;

  return currentPackage ? { ...currentPackage, Current: true } : null;
}

export async function getUserProductAccess(
  firmId: number
): Promise<ProductAwareUserRowData[] | null> {
  const data = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/subscriptions/users/products/?FirmId=${firmId}`,
    show_message: false,
    customized_success_message: "Successfully retrieved user product access.",
    customized_failure_message:
      "Unable to fetch user product access, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while fetching user product access, please contact support.",
    isblob: false,
  });

  return data?.success
    ? (data.data ?? []).map((row: ProductAwareUserRowData) =>
        normalizeUserProductAccessRow(row, firmId)
      )
    : null;
}

export async function getFirmSpecialPrivilegeRules(): Promise<FirmSpecialPrivilegesResponse | null> {
  const data = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/subscriptions/users/special-privileges/`,
    show_message: false,
    customized_success_message: "Successfully retrieved special privilege rules.",
    customized_failure_message:
      "Unable to fetch special privilege rules, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while fetching special privilege rules, please contact support.",
    isblob: false,
  });

  return data?.data ?? null;
}

export async function updateUserAccess(
  payload: UpdateUserAccessPayload
): Promise<any> {
  return makeCygnusApiCall({
    type: "POST",
    payload: {
      ...payload,
      FirmId: payload.FirmId ?? getStoredFirmId(),
    },
    url: `/subscriptions/roles/users/`,
    show_message: true,
    customized_success_message: "Successfully saved user access.",
    customized_failure_message:
      "Unable to save user access, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while saving user access, please contact support.",
    isblob: false,
  });
}

export async function updateUserProductAccess(
  firmId: number,
  userName: string,
  productCode: string,
  enabled: boolean
): Promise<boolean> {
  const data = await makeCygnusApiCall({
    type: "POST",
    payload: { FirmId: firmId, UserName: userName, ProductCode: productCode, Enabled: enabled, Cascade: true },
    url: `/subscriptions/users/products/`,
    show_message: true,
    customized_success_message: "Successfully updated user product access.",
    customized_failure_message:
      "Unable to update user product access, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while updating user product access, please contact support.",
    isblob: false,
  });

  return !!data?.success;
}

export async function updateUserProductRole(
  firmId: number,
  userName: string,
  productCode: string,
  roleId: number
): Promise<boolean> {
  const data = await makeCygnusApiCall({
    type: "POST",
    payload: { FirmId: firmId, UserName: userName, ProductCode: productCode, RoleId: roleId },
    url: `/subscriptions/users/products/roles/`,
    show_message: true,
    customized_success_message: "Successfully updated user product role.",
    customized_failure_message:
      "Unable to update user product role, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while updating user product role, please contact support.",
    isblob: false,
  });

  return !!data?.success;
}

export async function updateUserProductFeatures(
  firmId: number,
  userName: string,
  productCode: string,
  featureIds: number[]
): Promise<boolean> {
  const data = await makeCygnusApiCall({
    type: "POST",
    payload: { FirmId: firmId, UserName: userName, ProductCode: productCode, FeatureIds: featureIds },
    url: `/subscriptions/users/products/features/`,
    show_message: true,
    customized_success_message: "Successfully updated user product features.",
    customized_failure_message:
      "Unable to update user product features, please try again. If the problem persists, please contact support.",
    customized_error_message:
      "Error occurred while updating user product features, please contact support.",
    isblob: false,
  });

  return !!data?.success;
}
